import { useMutation } from '@tanstack/react-query';
import { ArrowRightIcon } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { Button } from '@/components/Buttons';
import { postProfileCompletedRewardMutationFn } from '@/queries/users';
import { trackMP } from '@/shared/mp';

/** How long the confirmation stands before the button offers to send it again. */
const SENT_CONFIRMATION_MS = 5000;

type ClaimState = 'idle' | 'sent' | 'resend';

const LABELS: Record<ClaimState, string> = {
  idle: 'Claim my free habitat',
  sent: 'Sent — check your inbox',
  resend: 'Resend the email',
};

/**
 * The Profile Completed Reward claim.
 *
 * Whether to render this at all is the caller's decision, taken from the *saved* profile — never
 * from what is currently typed into the form, because the email is a real-world side effect and
 * should not be triggered by a profile the server has not got.
 *
 * The claim state lives here and nowhere else: nothing is recorded server-side (zl-api ADR 0004), so
 * a reload offers the claim again, which is deliberate — the code is the same every time and people
 * lose emails. The mixpanel event is consequently the only count of rewards issued that exists,
 * which is why it fires on the click rather than on the response: a send that fails still happened,
 * and a count that silently drops those is worse than no count.
 */
const ClaimRewardButton = () => {
  const [claimState, setClaimState] = useState<ClaimState>('idle');
  const resendTimer = useRef<ReturnType<typeof setTimeout>>();

  const { mutate: claim, isPending } = useMutation({
    mutationFn: postProfileCompletedRewardMutationFn,
    onSuccess: () => {
      setClaimState('sent');
      resendTimer.current = setTimeout(() => setClaimState('resend'), SENT_CONFIRMATION_MS);
    },
    onError: (error) => {
      console.error('Failed to send the educator reward email:', error);
    },
  });

  const onClaim = () => {
    trackMP('educator-reward-claimed');
    claim();
  };

  useEffect(() => () => clearTimeout(resendTimer.current), []);

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="tertiary"
        size="xl"
        className="w-fit font-bold"
        loading={isPending}
        disabled={claimState === 'sent'}
        onClick={onClaim}
        data-component="claim-reward"
        data-claim-state={claimState}
      >
        {LABELS[claimState]}
        {claimState === 'idle' && <ArrowRightIcon className="preflight preflight-svg size-5" />}
      </Button>
      <p className="preflight preflight-p text-lg text-stone-500">Welcome to zoolife! Have a day pass, on us.</p>
    </div>
  );
};

export default ClaimRewardButton;
